import React, { useContext, useMemo } from "react";
// utils
import { overrideScreenNameInAnalyticsEvents, getCustomAnalyticsPayloadBasedOnCartSource } from "./utils";
// contexts
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

/**
 * AnalyticsOverrideProvider is a component that customizes analytics tracking based on cart source.
 * It wraps its children with a modified AnalyticsContext that includes cart-specific analytics data.
 *
 * Use cases:
 * 1. Modifying screen names in analytics events based on cart type (regular, gifting, etc.)
 * 2. Adding custom analytics payload specific to different cart sources
 * 3. Ensuring consistent analytics tracking across different cart flows
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped with modified analytics context
 * @returns {JSX.Element} Provider component with modified analytics context
 */
export const AnalyticsOverrideProvider = ({ children }: { children: React.ReactNode }) => {
    // Get the original analytics context that we'll modify
    const analyticsContextValue = useContext(AnalyticsContext);
    // Get cart flow data to determine the cart source
    const { data } = useContext(CartFlowContext);

    // Memoize the modified analytics context to prevent unnecessary re-renders
    const overrideAnalyticsContextValue = useMemo(() => {
        // Override the analytics events with custom payload based on cart source
        return overrideScreenNameInAnalyticsEvents(analyticsContextValue, {
            customAnalyticsPayload: getCustomAnalyticsPayloadBasedOnCartSource(data.cartSource),
        });
    }, [analyticsContextValue, data.cartSource]); // Only recompute when analytics context or cart source changes

    // Provide the modified analytics context to children
    return <AnalyticsContext.Provider value={overrideAnalyticsContextValue}>{children}</AnalyticsContext.Provider>;
};
